import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  updateLocationWithGPS,
  updateLocationWithIP,
  setGeoHash,
  setGpsPermission,
  incrementGpsRetryCount,
  resetGpsRetryCount,
  scheduleGpsRetry,
  setLoading,
  setError,
  setShowGpsBanner
} from '../store/slices/locationSlice';
import { setLocation } from '../store/slices/authSlice'; // Import pour compatibilité avec ancien système
import {
  encodeGeoHash,
  getCurrentPositionWithRetry,
  getLocationFromIP,
  isValidCoordinates,
  isCacheValid
} from '../services/locationService';
import { getAuth } from 'firebase/auth';
import { userService } from '../services/userService';

const auth = getAuth();

/**
 * Hook de vérification et récupération de localisation
 * Système GPS OBLIGATOIRE avec fallback IP temporaire
 *
 * Stratégie :
 * 1. Essayer GPS avec retry agressif (5 tentatives)
 * 2. Si échec GPS → fallback IP temporaire
 * 3. Si IP actif → retry GPS toutes les 5 minutes en arrière-plan
 * 4. Afficher GpsBanner tant que localisation IP est utilisée
 */
const useLocationCheck = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Sélecteurs Redux
  const locationState = useSelector(state => state.location);
  const authLocation = useSelector(state => state.auth.location);
  const { location, strategy, gpsPermission } = locationState;

  // État local
  const [isChecking, setIsChecking] = useState(false);

  // Refs pour cleanup proper
  const retryIntervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  // Détection du mode développement
  const isDevEnvironment = window.location.hostname === '192.168.31.222' ||
                           window.location.hostname === 'localhost';

  
  const isDev =  isDevEnvironment;

  const hasValidCoordinates = useCallback((locationData) => {
    if (!locationData?.coordinates || locationData.coordinates.length !== 2) {
      return false;
    }

    const [lng, lat] = locationData.coordinates;
    return isValidCoordinates(lat, lng);
  }, []);

  const syncAuthLocation = useCallback((locationData) => {
    if (!hasValidCoordinates(locationData)) {
      return;
    }

    const normalizedLocation = {
      type: 'Point',
      coordinates: locationData.coordinates,
      geoHash: locationData.geoHash || ''
    };

    if (
      authLocation?.coordinates?.[0] === normalizedLocation.coordinates[0] &&
      authLocation?.coordinates?.[1] === normalizedLocation.coordinates[1] &&
      (authLocation?.geoHash || '') === normalizedLocation.geoHash
    ) {
      return;
    }

    dispatch(setLocation(normalizedLocation));
  }, [authLocation, dispatch, hasValidCoordinates]);

  /**
   * Vérifier si la localisation actuelle est valide et récente
   */
  const isLocationValid = useCallback(() => {
    if (!hasValidCoordinates(location)) {
      return false;
    }

    // Vérifier si pas trop ancienne (> 24h)
    const cacheValid = isCacheValid(strategy.lastUpdate, 24);

    if (!cacheValid) {
      return false;
    }

    return true;
  }, [hasValidCoordinates, location, strategy]);

  /**
   * Sauvegarder la localisation sur le serveur
   */
  const saveLocationToServer = useCallback(async (locationData) => {
    const user = auth.currentUser;
    if (user) {
      try {
        await userService.updateLocation(user.uid, {
          type: 'Point',
          coordinates: locationData.coordinates,
          geoHash: locationData.geoHash
        });
      } catch (error) {
        console.error('[useLocationCheck] Error saving to server:', error);
      }
    }
  }, []);

  /**
   * NIVEAU 1 : Essayer GPS avec retry agressif
   */
  const tryGPS = useCallback(async () => {
    try {
      const result = await getCurrentPositionWithRetry({
        enableHighAccuracy: true,
        timeout: 45000,
        maximumAge: 30000
      }, 5); // 5 tentatives max

      if (result.success) {
        const { coordinates, accuracy } = result.data;
        const [lng, lat] = coordinates;
        const geoHash = encodeGeoHash(lat, lng);

        // Mettre à jour Redux (nouveau locationSlice)
        dispatch(updateLocationWithGPS({ coordinates, accuracy }));
        dispatch(setGeoHash(geoHash));
        dispatch(resetGpsRetryCount());

        // Mettre à jour authSlice pour compatibilité avec ancien système
        syncAuthLocation({
          type: 'Point',
          coordinates,
          geoHash
        });

        // Sauvegarder sur le serveur
        await saveLocationToServer({ coordinates, geoHash });

        return { success: true };
      } else {
        // Gérer les permissions
        if (result.error === 'permission_denied') {
          dispatch(setGpsPermission({
            status: 'denied'
          }));
        } else {
          dispatch(incrementGpsRetryCount());
        }

        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('[useLocationCheck] GPS error:', error);
      return { success: false, error: error.message };
    }
  }, [dispatch, saveLocationToServer, syncAuthLocation]);

  /**
   * NIVEAU 2 : Fallback IP temporaire
   */
  const tryIP = useCallback(async () => {
    try {
      const result = await getLocationFromIP();

      if (result.success) {
        const { coordinates, city, country } = result.data;
        const [lng, lat] = coordinates;
        const geoHash = encodeGeoHash(lat, lng);

        // Mettre à jour Redux (nouveau locationSlice - temporaire)
        dispatch(updateLocationWithIP({ coordinates, city, country }));
        dispatch(setGeoHash(geoHash));

        // Mettre à jour authSlice pour compatibilité avec ancien système
        syncAuthLocation({
          type: 'Point',
          coordinates,
          geoHash
        });

        // Sauvegarder sur le serveur
        await saveLocationToServer({ coordinates, geoHash });

        // Programmer retry GPS dans 5 minutes
        dispatch(scheduleGpsRetry());

        return { success: true };
      } else {
        return { success: false };
      }
    } catch (error) {
      console.error('[useLocationCheck] IP error:', error);
      return { success: false };
    }
  }, [dispatch, saveLocationToServer, syncAuthLocation]);

  /**
   * Fonction principale de vérification
   * @param {boolean} forceGPS - Forcer la tentative GPS
   */
  const checkLocation = useCallback(async (forceGPS = false) => {
    // Bypass en dev
    if (isDev) {
      return;
    }

    // Vérifier si localisation GPS déjà valide
    if (!forceGPS && isLocationValid() && !strategy.isTemporary) {
      syncAuthLocation(location);
      return;
    }

    setIsChecking(true);
    dispatch(setLoading(true));

    try {
      // Essayer GPS d'abord
      const gpsResult = await tryGPS();

      if (gpsResult.success) {
        setIsChecking(false);
        dispatch(setLoading(false));
        return;
      }

      // Si GPS échoue, essayer IP (temporaire)
      const ipResult = await tryIP();

      if (ipResult.success) {
        setIsChecking(false);
        dispatch(setLoading(false));
        return;
      }

      // Si tout échoue, rediriger vers /auth/location
      console.error('[useLocationCheck] Both GPS and IP failed');
      dispatch(setError('Unable to determine location'));
      navigate('/auth/location');

    } catch (error) {
      console.error('[useLocationCheck] Check error:', error);
      dispatch(setError(error.message));
      navigate('/auth/location');
    } finally {
      setIsChecking(false);
      dispatch(setLoading(false));
    }
  }, [isDev, isLocationValid, strategy, tryGPS, tryIP, dispatch, navigate, syncAuthLocation, location]);

  /**
   * Forcer retry GPS (appelé depuis GpsBanner ou Location.js)
   */
  const retryGPS = useCallback(async () => {
    await checkLocation(true);
  }, [checkLocation]);

  /**
   * Retry GPS continu si localisation IP active
   * Vérifie toutes les 5 minutes
   */
  useEffect(() => {
    // Ne rien faire en dev
    if (isDev) return;

    // Si localisation IP temporaire, retry GPS périodiquement
    if (strategy.isTemporary && strategy.mode === 'ip_temporary') {
      // Vérifier immédiatement
      const now = Date.now();
      const nextRetry = gpsPermission.nextRetryAt;

      if (nextRetry && now >= nextRetry) {
        checkLocation(true);
      }

      // Programmer retry périodique toutes les 5 minutes
      retryIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          checkLocation(true);
        }
      }, 5 * 60 * 1000); // 5 minutes

      return () => {
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
        }
      };
    }
  }, [isDev, strategy, gpsPermission, checkLocation]);

  /**
   * Synchroniser les deux sources persistées.
   * Les routes protégées historiques lisent auth.location, tandis que le
   * système GPS/IP écrit dans locationSlice.
   */
  useEffect(() => {
    if (hasValidCoordinates(location)) {
      syncAuthLocation(location);
      return;
    }

    if (hasValidCoordinates(authLocation)) {
      dispatch(updateLocationWithGPS({
        coordinates: authLocation.coordinates,
        accuracy: null
      }));
      dispatch(setGeoHash(authLocation.geoHash || ''));
    }
  }, [authLocation, dispatch, hasValidCoordinates, location, syncAuthLocation]);

  /**
   * Vérification initiale au montage
   */
  useEffect(() => {
    if (isDev) {
      // Définir une position mock pour le développement (Abidjan, Côte d'Ivoire)
      const mockCoordinates = [-3.6776489, 5.3106037]; // [lng, lat]
      const mockGeoHash = encodeGeoHash(5.3106037, -3.6776489);
      // Dispatch uniquement si pas de localisation déjà définie
      if (!location?.coordinates || location.coordinates.length !== 2) {
        // Mettre à jour locationSlice
        dispatch(updateLocationWithGPS({
          coordinates: mockCoordinates,
          accuracy: 50
        }));
        dispatch(setGeoHash(mockGeoHash));
        dispatch(setGpsPermission({ status: 'granted' }));

        // Mettre à jour authSlice pour compatibilité
        syncAuthLocation({
          type: 'Point',
          coordinates: mockCoordinates,
          geoHash: mockGeoHash
        });

        // Sauvegarder sur le serveur (même en dev pour tester)
        saveLocationToServer({ coordinates: mockCoordinates, geoHash: mockGeoHash });
      }

      return;
    }

    // Vérifier si on a besoin d'une localisation
    if (!isLocationValid()) {
      checkLocation();
    } else if (strategy.isTemporary) {
      // Si localisation IP, afficher le banner
      dispatch(setShowGpsBanner(true));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuté une seule fois au montage

  /**
   * Cleanup à la destruction
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      if (retryIntervalRef.current) clearInterval(retryIntervalRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  return {
    isChecking,
    location,
    strategy,
    gpsPermission,
    checkLocation,
    retryGPS,
    isLocationValid: isLocationValid()
  };
};

export default useLocationCheck;
